<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ServiceEntry } from '../api';
import ChosenCard from './ChosenCard.vue';
import DetailPopup from './DetailPopup.vue';
import { formatPrice } from '../price';

export type ServiceState = 'ServiceMode' | 'ServiceChosen' | 'ServiceNotChosen';

const props = defineProps<{
  state: ServiceState;
  items: ServiceEntry[];
  modelValue: string | null;
  displayPrice: boolean;
  showPrice?: (price?: string) => boolean;
}>();

const shouldShowPrice = (price?: string) => {
  if (!props.displayPrice) {
    return false;
  }
  if (props.showPrice) {
    return props.showPrice(price);
  }
  return !!price && Number(price) > 0;
};

const formatServiceSecondLine = (service: ServiceEntry) => {
  const parts = [`${service.duration} min`];
  if (shouldShowPrice(service.price)) {
    parts.push(formatPrice(+service.price));
  }
  return parts.join(' • ');
};

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
  (e: 'toggle-display-mode'): void;
}>();

const selectedService = computed(() => {
  return props.items.find((item) => item.uuid === props.modelValue) || null;
});

const detailItem = ref<ServiceEntry | null>(null);

function openDetail(service: ServiceEntry) {
  detailItem.value = service;
}

function selectFromPopup() {
  if (detailItem.value) {
    emit('update:modelValue', detailItem.value.uuid);
  }
  detailItem.value = null;
}
</script>

<template>
  <div :class="{ 'mbw-section-with-separator': state !== 'ServiceMode' }">
    <div v-if="state === 'ServiceMode' && selectedService">
    <div class="mbw-mode-title">
      {{ selectedService.name }}
    </div>
  </div>

  <div v-if="state === 'ServiceNotChosen'" class="mbw-mobile-overflow-icon">
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22" fill="none">
      <path d="M16.7501 15.4167C16.7501 16.8312 16.1882 18.1877 15.188 19.1879C14.1878 20.1881 12.8312 20.75 11.4167 20.75" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M14.0834 13.4167V12.75C14.0834 12.3964 13.9429 12.0573 13.6929 11.8072C13.4428 11.5572 13.1037 11.4167 12.7501 11.4167C12.3965 11.4167 12.0573 11.5572 11.8073 11.8072C11.5572 12.0573 11.4167 12.3964 11.4167 12.75" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M11.4167 12.75V12.0833C11.4167 11.7297 11.2762 11.3906 11.0261 11.1405C10.7761 10.8905 10.437 10.75 10.0833 10.75C9.72971 10.75 9.39057 10.8905 9.14053 11.1405C8.89048 11.3906 8.75 11.7297 8.75 12.0833V12.75" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M8.74992 12.4167V8.75002C8.74992 8.3964 8.60945 8.05726 8.3594 7.80721C8.10935 7.55716 7.77021 7.41669 7.41659 7.41669C7.06296 7.41669 6.72383 7.55716 6.47378 7.80721C6.22373 8.05726 6.08325 8.3964 6.08325 8.75002V15.4167" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M14.0833 13.4167C14.0833 13.0631 14.2237 12.7239 14.4738 12.4739C14.7238 12.2238 15.063 12.0833 15.4166 12.0833C15.7702 12.0833 16.1094 12.2238 16.3594 12.4739C16.6095 12.7239 16.7499 13.0631 16.7499 13.4167V15.4167C16.7499 16.8312 16.188 18.1877 15.1878 19.1879C14.1876 20.1881 12.8311 20.75 11.4166 20.75H10.0832C8.21658 20.75 7.08325 20.1767 6.08991 19.19L3.68991 16.79C3.46053 16.536 3.33763 16.2035 3.34666 15.8613C3.35568 15.5192 3.49594 15.1936 3.73839 14.952C3.98084 14.7104 4.30691 14.5713 4.64909 14.5635C4.99127 14.5557 5.32335 14.6797 5.57658 14.91L6.74991 16.0833" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M11.4167 0.75L14.0834 3.41667L11.4167 6.08334" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M0.75 3.41669H14.0834" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M3.41667 0.75L0.75 3.41667L3.41667 6.08334" stroke="#909090" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </div>
  <div class="mbw-service-list mbw-mobile-overflow" v-if="state === 'ServiceNotChosen'">
    <button
      v-for="service in items"
      :key="service.uuid"
      type="button"
      class="mbw-service"
      :class="{ 'mbw-service--active': modelValue === service.uuid }"
      @click="emit('update:modelValue', service.uuid)"
    >
      <div class="mbw-service-content">
        <div class="mbw-service-main">
          <div class="mbw-service-title">
            {{ service.name }}
          </div>
          <div v-if="service.duration || service.price" class="mbw-service-meta">
            <span v-if="service.duration">{{ service.duration }} min</span>
            <span v-if="service.duration && shouldShowPrice(service.price)"> • </span>
            <span v-if="shouldShowPrice(service.price)">{{ formatPrice(+service.price) }}</span>
          </div>
          <a href="javascript:void(0)" class="mbw-service-more-row" @click.prevent.stop="openDetail(service)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
            <span class="mbw-service-more">Więcej</span>
          </a>
        </div>
        <div class="mbw-service-status">
          <svg v-if="modelValue === service.uuid" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#017BC4"/>
            <path d="M6 12L10 16L18 8" stroke="white" stroke-width="2"/>
          </svg>
        </div>
      </div>
    </button>
  </div>

  <div
    v-if="state === 'ServiceChosen' && selectedService"
    class="mbw-practitioner-chosen"
  >
    <ChosenCard
      :first-line="selectedService.name"
      :second-line="formatServiceSecondLine(selectedService)"
      :muted-second-line="true"
      @change-click="emit('toggle-display-mode')"
    >
      <template #icon>
        <div class="mbw-datechosen-icon">
          <div class="mbw-datechosen-icon-bg">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V7"
                stroke="#3C3D41"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M21 17V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H17"
                stroke="#3C3D41"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3 7V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H7"
                stroke="#3C3D41"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V17"
                stroke="#3C3D41"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7.82808 13.07C7.29202 12.5092 6.994 11.7626 6.9966 10.9869C6.99919 10.2111 7.30219 9.46653 7.842 8.90939C8.38181 8.35224 9.11645 8.02586 9.89174 7.99875C10.667 7.97164 11.4227 8.24591 12.0001 8.76399C12.5775 8.24591 13.3331 7.97164 14.1084 7.99875C14.8837 8.02586 15.6184 8.35224 16.1582 8.90939C16.698 9.46653 17.001 10.2111 17.0036 10.9869C17.0062 11.7626 16.7082 12.5092 16.1721 13.07L12.7251 16.69C12.6317 16.7882 12.5193 16.8663 12.3947 16.9198C12.2702 16.9732 12.1361 17.0007 12.0006 17.0007C11.8651 17.0007 11.731 16.9732 11.6064 16.9198C11.4819 16.8663 11.3695 16.7882 11.2761 16.69L7.82808 13.07Z"
                stroke="#3C3D41"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </template>
    </ChosenCard>
  </div>

  </div>

  <DetailPopup
    :visible="!!detailItem"
    :title="detailItem?.name ?? ''"
    :description="detailItem?.description"
    @close="detailItem = null"
    @select="selectFromPopup"
  />
</template>

